/**
 * Normalizes a category name into a URL-friendly value
 * Converts spaces to hyphens and ampersands to 'and'
 *
 * @param category - The category name to normalize
 * @returns URL-friendly category value
 *
 * @example
 * normalizeCategoryValue("Invoice Management & AP") // "invoice-management-and-ap"
 */
export const normalizeCategoryValue = (category: string): string =>
  category.toLowerCase().replace(/\s+/g, '-').replace(/&/g, 'and')

/**
 * Creates a category object with both display name and URL value
 *
 * @param categoryName - The category display name
 * @returns Object with category_name and value properties
 *
 * @example
 * createCategoryObject("Spend Management")
 * // { category_name: "Spend Management", value: "spend-management" }
 */
export const createCategoryObject = (categoryName: string) => ({
  category_name: categoryName,
  value: normalizeCategoryValue(categoryName),
})

/**
 * Generates an array of category objects from a list of category names
 * Filters out any empty/falsy values
 *
 * @param categories - Array of category names
 * @returns Array of category objects
 */
export const createCategoryObjects = (categories: string[]) => categories.filter(Boolean).map(createCategoryObject)

import type {Locales} from 'src/common/constants'
import {CATEGORY_MAP} from 'src/common/hooks/useGetBlogPostCategory'

export const getLocalizedCategoryName = (englishName: string, locale: string): string => {
  const map = CATEGORY_MAP[locale as Locales]
  return map?.[englishName] || englishName
}

export const createLocalizedCategoryObject = (englishCategoryName: string, locale: string) => {
  const localizedName = getLocalizedCategoryName(englishCategoryName, locale)
  return {
    category_name: localizedName,
    value: normalizeCategoryValue(localizedName),
  }
}

export const createLocalizedCategoryObjects = (categories: string[], locale: string) =>
  categories.filter(Boolean).map((cat) => createLocalizedCategoryObject(cat, locale))

export const findEnglishCategoryByLocalizedSlug = (slug: string, locale: string): string | null => {
  const map = CATEGORY_MAP[locale as Locales]

  if (!map) {
    return null
  }

  for (const [englishName, localizedName] of Object.entries(map)) {
    if (normalizeCategoryValue(localizedName) === slug) {
      return englishName
    }
  }

  return null
}
